import {
  Box,
  CircularProgress,
  FormCheckBox,
  FormControllerContext,
  FormDatePicker,
  Stack,
  useTheme,
} from "common/components";
import { addFlowStepPrefix } from "common/utils";
import { BindsFlowData, BindsFlowStep } from "core/api";
import { useTranslation } from "i18n";
import { useContext, useState } from "react";
import { ProductLifecycleTimeline } from "../../../../components";
import { FlowStepComponentProps } from "../models";
import { StyledStepContentWrapper } from "../steps.styles";

export type BindsProps = FlowStepComponentProps<BindsFlowStep, BindsFlowData>;

/**
 * Binds step component to view/edit adjustment date and optional override bind settings.
 */
export function Binds({ control, data, flowStep, isLoading }: BindsProps) {
  const { t } = useTranslation(["common"]);
  const theme = useTheme();
  const [isLifecycleLoading, setIsLifecycleLoading] = useState(false);

  const {
    allowedToChangeAdjustmentDate,
    allowedToOverrideBinds,
    allowedToSupressFees,
    showProductLifecycleTimeline,
    previewMode,
  } = flowStep.config;

  const {
    adjustmentDate,
    overrideBinds,
    supressFees,
    productLifecycleData,
    currentDate,
  } = data;

  const { evaluateOnChange } = useContext(FormControllerContext);

  const handleAdjustmentDateChange = () => {
    if (showProductLifecycleTimeline) {
      setIsLifecycleLoading(true);
      // No need to pass an onComplete function to reset the isLifecycleLoading to false,
      // because the whole step gets re-mounted when the response is received
      // => state gets re-initialized.
      evaluateOnChange?.();
    }
  };

  const handleDemoDateChange = () => {
    // Trigger an evaluation request ONLY IF the ProductLifecycleTimeline
    // is displayed in this step, and previewMode is enabled
    if (showProductLifecycleTimeline && previewMode) {
      setIsLifecycleLoading(true);
      // No need to pass an onComplete function to reset the isLifecycleLoading to false,
      // because the whole step gets re-mounted when the response is received
      // => state gets re-initialized.
      evaluateOnChange?.();
    }
  };

  const [enableAdjustmentDate, setAdjustmentDateEnabled] = useState(
    allowedToChangeAdjustmentDate
  );

  const adjustmentDateFieldName = addFlowStepPrefix(flowStep, "adjustmentDate");
  const currentDateFieldName = addFlowStepPrefix(flowStep, "currentDate");

  return (
    <StyledStepContentWrapper>
      <Stack sx={{ width: "100%" }}>
        <FormDatePicker
          control={control}
          label={t("common:productLifeCycle.cursors.adjustedBindEndDate")}
          data-cy="adjustedBindEndDate"
          fieldName={adjustmentDateFieldName}
          defaultValue={adjustmentDate}
          required
          disabled={!enableAdjustmentDate || isLoading}
          onAccept={handleAdjustmentDateChange}
          onSelectedSectionsChange={(segment) => {
            if (segment === null) {
              // segment is null when the text input box of the Date field loses the focus
              handleAdjustmentDateChange();
            }
          }}
        />
        <Stack>
          <FormCheckBox
            control={control}
            fieldName={addFlowStepPrefix(flowStep, "overrideBinds")}
            label={t("common:overrideBinds")}
            defaultValue={overrideBinds}
            disabled={!allowedToOverrideBinds}
            onClick={() =>
              setAdjustmentDateEnabled(
                allowedToChangeAdjustmentDate || !enableAdjustmentDate
              )
            }
            sx={{ margin: 0 }}
          />
          <FormCheckBox
            size="small"
            control={control}
            fieldName={addFlowStepPrefix(flowStep, "supressFees")}
            label={t("common:supressFees")}
            defaultValue={supressFees}
            disabled={!allowedToSupressFees}
            dependsOn={{
              key: { key: "overrideBinds", variant: "" as any },
              disabledIf: false,
              resetIfDisabled: true,
            }}
            sx={{ margin: 0, marginLeft: 4 }}
          />
        </Stack>
        {showProductLifecycleTimeline && (
          <>
            {isLifecycleLoading ? (
              <CircularProgress />
            ) : (
              <Box display="block">
                {previewMode && (
                  <FormDatePicker
                    control={control}
                    label={t("common:productLifeCycle.currentDate")}
                    fieldName={currentDateFieldName}
                    defaultValue={currentDate}
                    required
                    disabled={!previewMode || isLifecycleLoading}
                    onAccept={handleDemoDateChange}
                    onSelectedSectionsChange={(segment) => {
                      if (segment === null) {
                        // segment is null when the text input box of the Date field loses the focus
                        handleDemoDateChange();
                      }
                    }}
                    slotProps={{
                      textField: {
                        sx: { width: "190px" },
                      },
                    }}
                  />
                )}
                <ProductLifecycleTimeline
                  productLifecycleData={productLifecycleData}
                  timelineZIndex={theme.zIndex.modal}
                />
              </Box>
            )}
          </>
        )}
      </Stack>
    </StyledStepContentWrapper>
  );
}
